import clsx from 'clsx';
import _ from 'lodash';
import { Field, useForm } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/core/Checkbox.atom';
import { FormFieldWrapper } from '@/formbuilder/FormFieldWrapper';
import { getFormFieldProps, getFormFieldWrapperProps } from '@/formbuilder/formbuilder.utilities';
import { Y_VALUE_BIN_MODES } from '@/tools/histogram/histogram.constants';
import { Icon, TextField } from '@seeqdev/qomponents';
import type { ValidatingFormComponent } from '@/formbuilder/formBuilder.types';

type MinMax = {
  min?: number;
  max?: number;
};

export type ValueBins = MinMax & {
  binSize?: number;
  numberOfBins?: number;
};

export interface ValueBinsIF extends ValidatingFormComponent<ValueBins> {
  component: 'ValueBinsFormComponent';
  yValueBinMode: string;
  setBinCreationMode: (mode: string) => void;
  rowClasses?: string;
  onBinSizeChange: (binSize: number | undefined) => void;
  onBinNumberChange: (binNumber: number | undefined) => void;
  onMaxChange?: (max: number | undefined) => void;
  onMinChange?: (min: number | undefined) => void;
  showMinMax?: boolean;
  sizeOfBinsMinMax?: MinMax;
  numberOfBinsMinMax?: MinMax;
  minMaxLabel?: string;
  minMaxLabelTooltip?: string;
  externalErrorText?: string;
  inputClassNames?: string;
  binSizeFormatter?: (value: number) => number;
  sizeOfBinsTooltip?: string;
  numberOfBinsTooltip?: string;
}

const isNotValid = (value?: number | null) => _.isNil(value) || _.isNaN(value);

export const ValueBinsFormComponent = (props: ValueBinsIF) => {
  const {
    testId = 'valueBins',
    name,
    yValueBinMode,
    setBinCreationMode,
    rowClasses,
    value = {} as ValueBins,
    onBinNumberChange,
    onBinSizeChange,
    onMaxChange,
    onMinChange,
    validation,
    showMinMax = true,
    numberOfBinsMinMax,
    sizeOfBinsMinMax,
    wrapperClassNames = '',
    minMaxLabel,
    minMaxLabelTooltip,
    externalErrorText,
    inputClassNames = 'width-75',
    binSizeFormatter,
    sizeOfBinsTooltip,
    numberOfBinsTooltip,
  } = props;

  const { t } = useTranslation();
  const formState = useForm().getState();

  const isBinSizeInValid = (binSize?: number | null) => !isNotValid(binSize) && (binSize as number) <= 0;

  const isNumberOfBinsInValid = (numberOfBins?: number | null) =>
    !isNotValid(numberOfBins) && (numberOfBins as number) <= 0;

  const isBinSizeAndNumberInValid = (value: ValueBins) => isNotValid(value?.binSize) && isNotValid(value?.numberOfBins);

  const hasMinMaxValues = (v: ValueBins) => !isNotValid(v.min) && !isNotValid(v.max);

  const invalidMinMax = hasMinMaxValues(value) && (value.min as number) >= (value.max as number);

  const defaultValidation = (value: ValueBins) =>
    isBinSizeAndNumberInValid(value) ||
    isBinSizeInValid(value?.binSize) ||
    isNumberOfBinsInValid(value?.numberOfBins) ||
    isNotValid(value?.min) ||
    isNotValid(value?.max) ||
    invalidMinMax;

  const appliedValidation: (value: ValueBins) => boolean = _.isFunction(validation) ? validation : defaultValidation;

  const showError = _.isFunction(validation)
    ? validation(value)
    : Boolean(
        (_.has(formState.errors, name) && _.get(formState.modified, name)) || formState.submitFailed || invalidMinMax,
      );

  const getCustomErrorText = ({ binSize, numberOfBins, min, max }: ValueBins) => {
    if (isBinSizeInValid(binSize)) {
      return 'AGGREGATIONS.INVALID_BIN_SIZE';
    }
    if (isNumberOfBinsInValid(numberOfBins)) {
      return 'AGGREGATIONS.INVALID_BIN_NUMBER';
    }
    if (min && max && min >= max) {
      return 'AGGREGATIONS.MIN_EXCEEDS_MAX';
    }
    if (externalErrorText) {
      return externalErrorText;
    }
    return undefined;
  };

  const includeZero = (value?: number | null): string | number => (isNotValid(value) ? '' : value!);

  const formatBinSizeForDisplay = (value?: number | null): string | number => {
    if (binSizeFormatter && !isNotValid(value)) {
      return binSizeFormatter(value!);
    }
    return includeZero(value);
  };

  const getValue = (value: string): number | undefined => (value === '' ? undefined : Number(value));

  const renderMinMaxLabel = () =>
    minMaxLabel ? (
      <div className="flexColumnContainer mt5 mb5">
        <span>{t(minMaxLabel)}</span>
        {minMaxLabelTooltip && (
          <Icon icon="fa-question-circle" extraClassNames="ml5 mt2" tooltip={t(minMaxLabelTooltip)} />
        )}
      </div>
    ) : null;

  return (
    <FormFieldWrapper
      testId={testId}
      {...getFormFieldWrapperProps(props, ['id', 'tooltip'])}
      showError={showError}
      customErrorText={getCustomErrorText(value)}
      wrapperClassNames={clsx('width-100percent', wrapperClassNames)}>
      <Field name={name} validate={appliedValidation}>
        {({ input, meta }) => {
          const { onChange, value: fieldValue = {} as ValueBins } = getFormFieldProps(
            formState,
            input,
            meta,
            props,
          ) as { onChange: (value: ValueBins) => void; value?: ValueBins };

          return (
            <>
              <table className="table table-condensed mb0">
                <tbody>
                  <tr className={rowClasses}>
                    <td>
                      <Checkbox
                        id="size"
                        classes="mt5"
                        label="AGGREGATIONS.SIZE_OF_BIN"
                        tooltip={sizeOfBinsTooltip}
                        isChecked={yValueBinMode === Y_VALUE_BIN_MODES.SIZE}
                        type="radio"
                        onChange={() => setBinCreationMode(Y_VALUE_BIN_MODES.SIZE)}
                      />
                    </td>
                    <td>
                      <TextField
                        autoComplete="off"
                        testId="binSize-input"
                        type="number"
                        size="sm"
                        extraClassNames={inputClassNames}
                        value={formatBinSizeForDisplay(fieldValue.binSize)}
                        min={sizeOfBinsMinMax?.min}
                        max={sizeOfBinsMinMax?.max}
                        onChange={(e) => {
                          const binSize = getValue(e.target.value);
                          onChange({ ...fieldValue, binSize });
                          onBinSizeChange(binSize);
                          setBinCreationMode(Y_VALUE_BIN_MODES.SIZE);
                        }}
                        showError={
                          showError && (isBinSizeInValid(fieldValue.binSize) || isBinSizeAndNumberInValid(fieldValue))
                        }
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <Checkbox
                        id="number"
                        classes="mt5"
                        label="AGGREGATIONS.NUMBER_OF_BINS"
                        tooltip={numberOfBinsTooltip}
                        isChecked={yValueBinMode === Y_VALUE_BIN_MODES.NUMBER}
                        type="radio"
                        onChange={() => setBinCreationMode(Y_VALUE_BIN_MODES.NUMBER)}
                      />
                    </td>
                    <td>
                      <TextField
                        autoComplete="off"
                        testId="numberOfBins-input"
                        type="number"
                        size="sm"
                        extraClassNames={inputClassNames}
                        value={includeZero(fieldValue.numberOfBins)}
                        min={numberOfBinsMinMax?.min}
                        max={numberOfBinsMinMax?.max}
                        onChange={(e) => {
                          const numberOfBins = getValue(e.target.value);
                          onChange({
                            ...fieldValue,
                            numberOfBins,
                          });
                          onBinNumberChange(numberOfBins);
                          setBinCreationMode(Y_VALUE_BIN_MODES.NUMBER);
                        }}
                        showError={
                          showError &&
                          (isNumberOfBinsInValid(fieldValue.numberOfBins) || isBinSizeAndNumberInValid(fieldValue))
                        }
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
              {showMinMax && (
                <div className="flexRowContainer">
                  {renderMinMaxLabel()}
                  <div className="flexFill flexColumnContainer flexAlignCenter mt5" data-testid={`${testId}MinMax`}>
                    <span className="pr10">{t('AGGREGATIONS.MIN')}</span>
                    <TextField
                      testId={`${testId}Min`}
                      autoComplete="off"
                      type="number"
                      size="sm"
                      extraClassNames="width-75"
                      value={includeZero(fieldValue.min)}
                      onChange={(e) => {
                        const min = getValue(e.target.value);
                        onChange({ ...fieldValue, min });
                        onMinChange?.(min);
                      }}
                      showError={
                        showError &&
                        (isNotValid(fieldValue.min) ||
                          (hasMinMaxValues(fieldValue) && fieldValue.min! > fieldValue.max!))
                      }
                    />
                    <span className="pl10 pr10">{t('AGGREGATIONS.MAX')}</span>
                    <TextField
                      testId={`${testId}Max`}
                      autoComplete="off"
                      type="number"
                      size="sm"
                      extraClassNames="width-75"
                      value={includeZero(fieldValue.max)}
                      onChange={(e) => {
                        const max = getValue(e.target.value);
                        onChange({ ...fieldValue, max });
                        onMaxChange?.(max);
                      }}
                      showError={
                        showError &&
                        (isNotValid(fieldValue.max) ||
                          (hasMinMaxValues(fieldValue) && fieldValue.min! > fieldValue.max!))
                      }
                    />
                  </div>
                </div>
              )}
            </>
          );
        }}
      </Field>
    </FormFieldWrapper>
  );
};
